import SettingsService from '../services/SettingsService';
import axios from 'axios';
import { getToken } from '../Upshow';
import UpshowLogger from '../Logger';
import ScriptService from '../services/ScriptService';
import MediaService from '../services/MediaService';
import SpotlightService from '../services/SpotlightService';
import DisplayRulesService from '../services/DisplayRulesService';
import * as UP from '../Upshow';
import ScreenService from '../services/ScreenService';
import StateService from '../services/StateService';
import HwFeatureService from '../services/HwFeatureService';
import { SmartPlaylist } from '@upshow/smart-playlist';

const media_states = ['upshownow', 'jukin', 'trivia'];

export const nullOrTrue = (param) => param || param === null;

export const getSocialBrandKitStyle = () => {
    const { social_accent_color, social_background, logo } = SettingsService.getBrandKit();

    return {
        logo,
        backgroundColor: social_background,
        color: social_accent_color
    };
};

/**
 * Get authentication cookies from service
 * @param {string} url
 */
export const setAuthCookies = (url) => {
    return axios.get(`${url}`, {
        headers: {
            'x-access-token': getToken()
        },
        withCredentials: true
    })
        .catch(error => {
            UpshowLogger.error('Unable to sign URL', error, { url });
            throw Error;
        });
};

/**
 * Given a URL, this function adds parameters for identifying device and org if the URL seems to belong to UPshow
 * @param {string} url
 * @return {string}
 */
export function addDeviceOrgToURL (url) {
    if (!url.match(/\.upshow\.tv\//)) return url;

    let parsedUrl = new URL(url);

    parsedUrl.searchParams.set('device_id', SettingsService.getSetting('device_id'));
    parsedUrl.searchParams.set('org_id', SettingsService.getSetting('organization_id'));

    return parsedUrl.toString();
}

/**
 * Given a sequence, this function returns the actual sequence for smartPlaylist
 * @param {object} sequence
 * @return {object}
 */
function getSmartSequence (sequence) {
    let statesPercentage;
    const metadata = sequence.metadata;

    if (metadata) {
        statesPercentage = metadata.statesPercentage;
    } else {
        const active_smart_script = _.get(ScriptService, 'scripts.active_smart_script', null);
        statesPercentage = active_smart_script[0].metadata.statesPercentage;
    }

    return SmartPlaylist.getSmartScript(statesPercentage, getAvailableStates());
}

/**
 * Given a sequence, this function detects if there is no content available
 * @param {object[]} allSpotlights
 * @param {object[]} upshownow
 * @param {object[]} media_channel
 * @param {object[]} applicationInstances
 * @param {object[]} trivia
 * @param {boolean} hasWatchToWinSettings
 * @return {boolean}
 */
export const checkScriptContent = (allSpotlights, upshownow, media_channel, applicationInstances, trivia, hasWatchToWinSettings, scheduleScript = null) => {
    let sequence = [];
    if(scheduleScript) {
        sequence = scheduleScript.script.Sequence;
        const smartSequences = scheduleScript.script.sequence.filter(seq => seq.state === 'smart') ?? [];
        if(smartSequences.length > 0) {
            sequence = getSmartSequence(smartSequences[0]);
        }
    } else {
        sequence = ScriptService.getScript();
    }

    const availableAppInstancesIds = applicationInstances?.map(s => s.id);
    const availableMediaChannelIds = media_channel.map(s => s.id);
    const availableUpshowNowIds = upshownow?.map(s => s.id);
    const availableSpotlightsIds = allSpotlights.filter(s => !s.TrackingCode && !s.properties.includes('boh_spotlight')).map(s => s.id);
    const availableQRCodeIds = allSpotlights.filter(s => !!s.TrackingCode && !s.properties.includes('boh_spotlight')).map(s => s.id);
    
    const hasSocial = HwFeatureService.isSupported('social');

    if (ScreenService.isVertical) {
        return checkVerticalContent(sequence, availableSpotlightsIds, availableQRCodeIds, availableAppInstancesIds);
    }
    for (const item of sequence) {
        if (item.state === 'spotlight') {
            if (checkSpotlight(item, availableSpotlightsIds, availableQRCodeIds)) return true;
        } else if (item.state === 'upshownow' && availableUpshowNowIds.length > 0) {
            if (foundValidContent(availableUpshowNowIds, item)) return true;
        } else if (item.state === 'jukin' && media_channel.length > 0) {
            if (foundValidContent(availableMediaChannelIds, item)) return true;
        } else if (hasSocial && item.state === 'socialgrid' || item.state === 'socialzoom') {
            if (SettingsService.hasFeature('social') && SettingsService.hasTrueSetting('direct_share')) return true;
        } else if ((item.state === 'application' || item.state === 'app-application') && availableAppInstancesIds.length > 0) {
            if (foundValidContent(availableAppInstancesIds, item)) return true;
        } else if (item.state === 'trivia') {
            if (SettingsService.hasFeature('dr_time_based')) {
                const validMedia = MediaService.getValidMedia('trivia');
                if (validMedia.length > 0) return true;
            }

            const activeScheduleTrivia = MediaService.getAllActive(['trivia']);
            if (trivia.length > 0 && activeScheduleTrivia.length > 0) return true;
        } else if (item.state === 'watchtowin') {
            if (hasWatchToWinSettings) return true;
        } else if (item.state === 'vistar') {
            return true;
        } else if (item.state === 'iframe') {
            return true;
        }
    }

    return false;
};

const foundValidContent = (availableIds, { metadata, state, type }) => {
    // Display rules
    if (SettingsService.hasFeature('dr_time_based')) {
        let availableContent;
        if (state === 'spotlight') {
            let currentIsoDate = UP.getCurrentISODate();
            const ruleListValidator = DisplayRulesService.getRulesValidator(currentIsoDate);
            availableContent = SpotlightService.spotlights.filter(spotlight => ruleListValidator(spotlight.time_based_display_rules) && availableIds.includes(spotlight.id));
        }
        if (media_states.includes(state)) {
            availableContent = MediaService.getValidMedia(state);
        }
        if (state === 'application' && type === 'sportsbook') {
            availableContent = MediaService.getValidMedia('sportsbook');
        } else if (state === 'application' || state === 'app-application') {
            availableContent = MediaService.getValidMedia(type === 'plugin' ? 'plugin' : 'application');
        }

        if (availableContent) {
            //Granular
            if (Array.isArray(metadata?.id) && metadata.id.length > 0) {
                const availableIds = availableContent.map(s => s.id);
                return metadata.id.some((id) => availableIds.includes(id));
            }

            //No granular
            return availableContent.filter(content => !content.is_only_playlist).length > 0;
        }

        return true;
    }

    // Granular
    if (Array.isArray(metadata?.id) && metadata.id.length > 0) return metadata.id.some((id) => availableIds.includes(id));

    // Scheduled content
    if (state === 'spotlight') {
        //get current datetime to filter spotlights by schedule
        const currentIsoDate = UP.getCurrentISODate();
        const requiredProperties = ScreenService.isVertical ? ['clockwise_rotated'] : ['fullscreen'];
        const activeFullscreenSpotlights = SpotlightService.filterSchedules(SpotlightService.schedules, currentIsoDate, requiredProperties, false);
        const availableQRInSchedule = activeFullscreenSpotlights.filter(s => !!s.TrackingCode && !s.properties.includes('boh_spotlight'));
        const availableSpotlightsInSchedule = activeFullscreenSpotlights.filter(s => !s.TrackingCode && !s.properties.includes('boh_spotlight'));

        if (type === 'qr_code') {
            return availableQRInSchedule.length > 0;
        } else if (type === 'announcement') {
            return availableSpotlightsInSchedule.length > 0;
        } else if (type === 'boh_spotlight') {
            return false;
        } else {
            return availableSpotlightsInSchedule.length > 0 || availableQRInSchedule.length > 0;
        }
    }

    if (media_states.includes(state)) {
        const activeScheduleMedia = MediaService.getAllActive([state]);
        return activeScheduleMedia.length > 0;
    }

    if (state === 'application' || state === 'app-application') {
        const activeScheduleMediaApplication = MediaService.getAllActive(['application']);
        return activeScheduleMediaApplication.length > 0;
    }

    return true;
};

const checkVerticalContent = (sequence, availableSpotlightsIds, availableQRCodeIds, availableAppInstancesIds) => {
    for (const item of sequence) {
        if (item.state === 'spotlight') {
            if (checkSpotlight(item, availableSpotlightsIds, availableQRCodeIds)) return true;
        } else if (item.state === 'socialgrid' || item.state === 'socialzoom') {
            if (SettingsService.hasFeature('social') && SettingsService.hasTrueSetting('direct_share')) return true;
        } else if ((item.state === 'application' || item.state === 'app-application') && availableAppInstancesIds.length > 0) {
            if (foundValidContent(availableAppInstancesIds, item)) return true;
        } 
    }
    return false;
};

const checkSpotlight = (item, availableSpotlightsIds, availableQRCodeIds) => {
    if (item.type === 'qr_code' && availableQRCodeIds.length > 0) {
        if (foundValidContent(availableQRCodeIds, item)) return true;
    } else if (item.type === 'announcement' && availableSpotlightsIds.length > 0) {
        if (foundValidContent(availableSpotlightsIds, item)) return true;
    } else if (item.type === 'boh_spotlight') {
        return false;
    } else if (!item.type && availableSpotlightsIds.length > 0) {
        if (foundValidContent(availableSpotlightsIds, item)) return true;
    } else if (!item.type && availableQRCodeIds.length > 0) {
        if (foundValidContent(availableQRCodeIds, item)) return true;
    }
};

const hasPlayableMediaItems = (state) => {
    if (SettingsService.hasFeature('dr_time_based')) {
        return MediaService.getValidMedia(state).some(content => !content.is_only_playlist);
    }

    return MediaService.getAllActive([state]).some(content => !content.is_only_playlist);
};

const getScheduledApplications = () => {
    if (SettingsService.hasFeature('dr_time_based')) {
        return [...MediaService.getValidMedia('plugin'), ...MediaService.getValidMedia('sportsbook')]
            .filter(content => !content.is_only_playlist);
    }
    return MediaService.media
        .filter(content => content.type === 'application' && !content.is_only_playlist);
};

const hasPlayableApplications = (type = 'plugin') => {
    return getScheduledApplications().some(content => {
        const feedType = content.application?.feedType ?? content.applicationInstance?.application?.feedType;
        return type === 'sportsbook'
            ? feedType === 'SPORTSBOOK'
            : feedType !== 'SPORTSBOOK';
    });
};

const getAvailableSpotlightContent = () => {
    const activeFullscreenSpotlights = SpotlightService.getActiveFullScreenSpotlights(false);

    return {
        qrCampaigns: activeFullscreenSpotlights.filter(spotlight =>
            !!spotlight.TrackingCode && !spotlight.properties?.includes('boh_spotlight')),
        announcements: activeFullscreenSpotlights.filter(spotlight =>
            !spotlight.TrackingCode && !spotlight.properties?.includes('boh_spotlight'))
    };
};

const getEntertainmentStates = features => {
    const has = feature => features.includes(feature);
    const states = [];

    if ((has('jukin') || has('media_channel_non_premium') || has('media_channel_nhl'))
        && hasPlayableMediaItems('jukin')) {
        states.push('jukin');
    }
    if (has('upshow_now') && hasPlayableMediaItems('upshownow')) {
        states.push('upshownow');
    }
    if (has('upshow_games') && StateService.hasWatchToWinSettings) {
        states.push('watchtowin');
    }
    if (has('upshow_games') && SettingsService.hasUiSetting('game_id') && !ScreenService.isVertical && hasPlayableMediaItems('trivia')) {
        states.push('trivia');
    }
    if (has('sportsbook') && hasPlayableApplications('sportsbook')) {
        states.push('sportsbook');
    }

    return states;
};

/**
 * Returns states that are available to show
 * @return {object}
 */
export function getAvailableStates () {
    const features = Object.keys(SettingsService.settings?.features ?? {});
    const { qrCampaigns, announcements } = getAvailableSpotlightContent();
    const categoryStates = {
        social: [],
        entertainment: getEntertainmentStates(features),
        qr_campaigns: [],
        spotlights: [],
        plugins: [],
    };
    if (features.includes('social') && SettingsService.hasTrueSetting('direct_share')) {
        categoryStates.social = ['socialgrid', 'socialzoom'];
    }
    if (features.includes('campaigns') && qrCampaigns.length > 0) {
        categoryStates.qr_campaigns = ['qr_campaign'];
    }
    if (features.includes('spotlights') && announcements.length > 0) {
        categoryStates.spotlights = ['spotlight'];
    }
    if (hasPlayableApplications('plugin')) {
        categoryStates.plugins = ['application'];
    }

    return categoryStates;
};

export function shouldLog (urlParam) {
    const url = new URL(urlParam);

    if(!url.searchParams.has('enable_logs')) return true;

    return url.searchParams.get('enable_logs') === 'true';
}
